import classNames from 'classnames';
import ProgressIndicator from 'hellospa/foundation/control/progress-indicator';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import { Actions, FilePollResponseStatus, isErrorStatus, UserFile } from 'hellospa/page/prep-and-send/data/types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as hsEmbedded from 'common/utils/messages';
import hsImg from './grow-loading.png';
import dbxImg from './signature.png';
import * as styles from './index.scss';
import HelloModal from 'common/components/hello-modal';
import Button from 'hellospa/foundation/control/button';
import { Dispatch } from 'hellospa/redux/types';

const messages = defineMessages({
  stillLoading: {
    id: '',
    description: 'shows on a loding screen if it takes more than 10 seconds',
    defaultMessage: 'Hold tight! We are still preparing your documents',
  },
  preparing: {
    id: '',
    description: 'shows on a loading screen',
    defaultMessage: 'Preparing your documents...',
  },
  modalTitle: {
    id: '',
    description: 'Modal title',
    defaultMessage: "Couldn't convert documents",
  },
  integrationBody: {
    id: '',
    description: 'Modal body',
    defaultMessage: 'Your documents could not be converted. Please try again',
  },
  close: {
    id: '',
    description: 'Close button on a modal',
    defaultMessage: 'close',
  },
  modalBody: {
    id: '',
    description: 'Modal body',
    defaultMessage: "Your documents couldn't be converted. Please go to the <strong>Select documents</strong> page to check the errors and try uploading again.",
  },
  modalButton: {
    id: '',
    description: 'Button text',
    defaultMessage: 'Select Documents',
  },
});

type ErrorModalProps = {
  isOpen: boolean,
}
function ErrorModal({ isOpen }: ErrorModalProps) {
  const dispatch = useDispatch<Dispatch>();
  const intl = useIntl();
  const transmissionGroupGuid = useSelector(selectors.getTransmissionGroupGuid);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);
  const apiApp = useSelector(selectors.getApiApp);

  const workflowHack = React.useCallback((e) => {
    /**
     * I setup the button to use the router before I remembered we aren't
     * routing correctly in P&S. Instead of the router driving which page
     * you're on, the stepper has some code that looks at `currentPath` and if
     * you aren't at that route, it will move you there. This preventDefault stops
     * React Router from responding to this click. instead, we set a new `currentPath` and the
     * stepper will pick up the new value and route to the desired page.
     *
     * I think to remove this we need `nextPageInWorkflow` to be able to route
     * to the correct URL. Since it's a Redux action it doesn't currently have
     * access to the router.
     */
    e.preventDefault();
    dispatch({
      type: Actions.UpdateMeta,
      payload: {
        currentPath: 'documents',
      },
    });
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    if (apiApp) {
      hsEmbedded.postMessage({ type: hsEmbedded.messages.USER_CANCEL_REQUEST }, apiApp.parentUrl);
    }
  }, [apiApp]);

  if (isDeepIntegration) {
    return (
      <HelloModal
        isOpen={isOpen}
        contentLabel={intl.formatMessage(messages.modalTitle)}
        showCloseIcon={false}
        buttons={[
          <Button key="btn" onClick={handleClose}>
            {intl.formatMessage(messages.close)}
          </Button>,
        ]}
      >
        {intl.formatMessage(messages.integrationBody)}
      </HelloModal>
    );
  }

  return (
    <HelloModal
      isOpen={isOpen}
      contentLabel={intl.formatMessage(messages.modalTitle)}
      showCloseIcon={false}
      buttons={[
        <Button key="btn" onClick={workflowHack} to={`/prep-and-send/${transmissionGroupGuid}/documents`}>
          {intl.formatMessage(messages.modalButton)}
        </Button>,
      ]}
    >
      <span className={styles.modalBody}>
        {intl.formatMessage(messages.modalBody, {
          strong: function strong(...text) {
            return <span key="strong" className={styles.strong}>{text}</span>;
          },
        })}
      </span>
    </HelloModal>
  );
}

export function computePercent(files: UserFile[]) {
  const { pages, progress } = files.reduce((state, file) => {
    // Files that are still processing have a .total and completed files have
    // a .pageCount
    const originalTotalPages = ('total' in file && file.total) || file.pageCount || 0;

    // This file hasn't finished uploading, so skip it
    if (originalTotalPages === 0) {
      return state;
    }

    // default to 1, to prevent division by 0
    const totalPages = originalTotalPages || 1;
    let fileProgress = file.progress || 0;

    if (isErrorStatus(file.status)) {
      return state;
    }

    if (file.status === FilePollResponseStatus.Ok) {
      fileProgress = totalPages;
    }
    return {
      pages: state.pages + totalPages,
      progress: state.progress + fileProgress,
    };
  }, { pages: 0, progress: 0 });

  // If none of the files have any pages they must still be uploading.
  if (pages === 0) {
    return 10;
  }

  const percent = Math.floor(progress / pages * 100);
  return Math.max(10, percent);

}

type Props = {
  files: UserFile[],
}

export default function Loading({ files }: Props) {
  const intl = useIntl();
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);
  const [showMessage, setShowMessage] = React.useState(false);
  React.useEffect(() => {
    if (!showMessage) {
      const timeout = setTimeout(() => {
        setShowMessage(true);
      }, 10000);

      return () => clearTimeout(timeout);
    }
    return () => { };
  }, [showMessage]);

  const percent = React.useMemo(
    () => computePercent(files),
    [files],
  );

  const hasErrors = React.useMemo(() => {
    const erroredFile = files.find((file) => isErrorStatus(file.status));
    return erroredFile != null;
  }, [files]);


  return (
    <div className={styles.loading}>
      <ErrorModal isOpen={hasErrors} />

      <div className={classNames(styles.stillLoading, { [styles.show]: showMessage })} >
        {intl.formatMessage(messages.stillLoading)}
      </div>

      <img
        src={isDeepIntegration ? dbxImg : hsImg}
        alt=""
        className={styles.image}
        data-qa-ref={isDeepIntegration ? 'dbx-signature-image' : 'hs-signature-image'} />

      <div className={styles.preparing}>
        {intl.formatMessage(messages.preparing)}
      </div>

      <ProgressIndicator
        className={styles.progressBar}
        showCancelButton={false}
        showLoadingMessage={false}
        progress={percent / 100} />

      <div className={styles.percent}>
        {percent}%
      </div>
    </div>
  );
}
